import {makeApiCall} from '../core';
import { Invoice as SharedInvoice, InvoiceLineItem as SharedInvoiceLineItem } from '@ruta/shared';
import type { ItemEnvelope } from '@ruta/shared';

// =====================
// Invoices API
// =====================

// Extended Invoice type with webapp-specific fields
export interface Invoice extends Omit<SharedInvoice, 'lineItems'> {
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  subtotal: number;
  tax: number;
  total: number;
  lineItems: InvoiceLineItem[];
  serviceDate?: string;
  serviceAddress?: string;
  metadata?: {
    serviceTemplateName?: string;
    completedBy?: string;
    duration?: number;
    photos?: string[];
  };
  // Legacy fields for compatibility
  serviceTemplateName?: string;
  visitDetails?: {
    completedAt: string;
    totalServiceTime?: number;
  };
  transactionId?: string;
  processingFees?: {
    totalFee: number;
  };
  paymentError?: string;
}

// Extended InvoiceLineItem type with webapp-specific fields
export interface InvoiceLineItem extends SharedInvoiceLineItem {
  id: string;
  type: 'service' | 'option' | 'labor' | 'service-option' | 'minimum-adjustment' | 'property-metrics';
  quantity: number;
  rate: number;
  details?: string;
}

export interface InvoiceSummary {
  totalCount: number;
  totalAmount: number;
  pendingCount: number;
  pendingAmount: number;
  paidCount: number;
  paidAmount: number;
  failedCount: number;
  failedAmount: number;
}

export interface InvoicesResponse {
  invoices: Invoice[];
  summary: InvoiceSummary;
}

export interface InvoiceFilters {
  status?: string;
  limit?: string;
}

export const getInvoices = async (filters?: InvoiceFilters): Promise<InvoicesResponse> => {
  const params: Record<string, string> = {
    limit: filters?.limit || '50',
  };

  if (filters?.status && filters.status !== 'all') {
    params.status = filters.status;
  }

  const queryParams = new URLSearchParams(params);
  const res = await makeApiCall<ItemEnvelope<InvoicesResponse>>('GET', `/invoices?${queryParams}`);
  return res.data;
};

export const getInvoice = async (invoiceId: string): Promise<{ invoice: Invoice }> => {
  const res = await makeApiCall<ItemEnvelope<{ invoice: Invoice }>>('GET', `/invoices/${invoiceId}`);
  return res.data;
};

// Fetch invoice data generated from a visit (signed in; the API takes the org from the token).
export const getVisitInvoice = async (visitId: string): Promise<{ invoice: Invoice }> => {
  const res = await makeApiCall<ItemEnvelope<Invoice>>('GET', `/visits/${visitId}/invoice`);
  return { invoice: res.data };
};
